import { useEffect, useState } from 'react';
import { Autocomplete, Box, Grid, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { FormDrawer, FormDrawerGroup } from '../../../../../components/form-drawer';
import { DateField, FieldError } from '../../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../../components/brazilian-number-input';
import { ICommitmentAddEditRequest } from '../../../../../model/sge/commitment/request/commitment-add-edit-request';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { ISupplierPagedResponse } from '../../../../../model/sge/supplier/response/supplier-paged-response';
import { formatDocument } from '../../../../../utils/document';
import { hasErrors } from '../../../../../utils/validation';
import { CommitmentForm, toCommitmentForm, validateCommitment } from './amendment-detail-form-utils';
import { ProgressStat, ProgressSummary } from './progress-stat';

interface CommitmentDrawerProps {
    open: boolean;
    initial?: ICommitmentResponse;
    suppliers: ISupplierPagedResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: ICommitmentAddEditRequest) => void;
}

function supplierLabel(supplier: ISupplierPagedResponse): string {
    const documentNumber = supplier.cnpj || supplier.cpf;
    return `${supplier.personName || ''}${documentNumber ? ` - ${formatDocument(documentNumber)}` : ''}`;
}

function matchesSupplier(supplier: ISupplierPagedResponse, term: string): boolean {
    return [supplier.personName, supplier.cnpj, supplier.cpf].some((value) => value?.toLowerCase().includes(term));
}

export function CommitmentDrawer({ open, initial, suppliers, isSaving, onClose, onSave }: CommitmentDrawerProps) {
    const [form, setForm] = useState<CommitmentForm>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateCommitment(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toCommitmentForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<CommitmentForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave(form as ICommitmentAddEditRequest);
    }

    return (
        <FormDrawer
            open={open}
            icon={<ReceiptLongIcon />}
            title={isEdit ? 'Editar empenho' : 'Novo empenho'}
            subtitle={initial?.commitmentNumber ? `Empenho Nº ${initial.commitmentNumber}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar empenho'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            {initial && (
                <ProgressSummary>
                    <ProgressStat label="Valor liquidado" current={initial.totalLiquidatedValue || 0} total={form.committedAmount || 0} color="warning" />
                    <ProgressStat label="Valor pago" current={initial.totalPaidValue || 0} total={form.committedAmount || 0} color="success" />
                </ProgressSummary>
            )}

            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Nº do empenho</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.commitmentNumber || ''} onChange={(e) => update({ commitmentNumber: e.target.value })} error={!!errors.commitmentNumber} />
                        <FieldError message={errors.commitmentNumber} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <DateField label="Data do empenho" required value={form.commitmentDate} onChange={(value) => update({ commitmentDate: value || undefined })} error={errors.commitmentDate} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <DateField label="Data de publicação" required value={form.publicationDate} onChange={(value) => update({ publicationDate: value || undefined })} error={errors.publicationDate} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Fornecedor e pagamento">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <InputLabel required>Fornecedor</InputLabel>
                        <Autocomplete
                            fullWidth
                            options={suppliers}
                            value={suppliers.find((s) => s.id === form.supplierId) || null}
                            onChange={(_event, supplier) => update({ supplierId: supplier?.id })}
                            getOptionLabel={supplierLabel}
                            filterOptions={(options, { inputValue }) => {
                                const term = inputValue.toLowerCase().trim();
                                return term ? options.filter((option) => matchesSupplier(option, term)) : options;
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.personName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatDocument(option.cnpj || option.cpf)}</Typography>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Busque por nome ou CNPJ/CPF..." autoComplete="off" error={!!errors.supplierId} />
                            )}
                            noOptionsText="Nenhum fornecedor encontrado"
                        />
                        <FieldError message={errors.supplierId} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <InputLabel required>Valor empenhado</InputLabel>
                        <BrazilianNumberInput
                            fullWidth
                            value={form.committedAmount}
                            onChange={(committedAmount) => update({ committedAmount })}
                            decimals={2}
                            prefix="R$"
                            placeholder="0,00"
                            error={!!errors.committedAmount}
                        />
                        <FieldError message={errors.committedAmount} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Conta bancária</InputLabel>
                        <OutlinedInput fullWidth multiline rows={3} value={form.bankAccountUsed || ''} onChange={(e) => update({ bankAccountUsed: e.target.value })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Vínculo">
                <InputLabel required>Objeto de vínculo</InputLabel>
                <OutlinedInput fullWidth multiline rows={3} value={form.objectLink || ''} onChange={(e) => update({ objectLink: e.target.value })} error={!!errors.objectLink} />
                <FieldError message={errors.objectLink} />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
